import { colorsPalette } from './colorsPalette';
import type { AlertProps } from '@mui/material';
import type { ComponentsVariants, Theme } from '@mui/material/styles';

interface VariantStyles {
    backgroundColor: string;
    borderColor: string;
    iconColor: string;
    titleColor: string;
}

type AlertColor = Required<AlertProps>['color'];

const getVariantStylesMapping = (theme: Theme): Record<AlertColor, VariantStyles> => ({
    error: {
        backgroundColor: theme.colors.background['danger-subtle'],
        borderColor: theme.colors.border['danger-subtle'],
        iconColor: theme.colors.text.danger,
        titleColor: theme.colors.text.danger,
    },
    info: {
        backgroundColor: colorsPalette.blackSolid[3],
        borderColor: theme.palette.divider,
        iconColor: theme.palette.text.secondary,
        titleColor: theme.palette.text.primary,
    },
    success: {
        backgroundColor: theme.colors.background['success-subtle'],
        borderColor: theme.colors.border['success-subtle'],
        iconColor: theme.colors.text.success,
        titleColor: theme.colors.text.success,
    },
    warning: {
        backgroundColor: theme.colors.background['warning-subtle'],
        borderColor: theme.colors.border['warning-subtle'],
        iconColor: theme.colors.text.warning,
        titleColor: theme.colors.text.warning,
    },
    promotion: {
        backgroundColor: colorsPalette.purpleSolid[4],
        borderColor: colorsPalette.purpleAlpha[16],
        iconColor: colorsPalette.purpleSolid[100],
        titleColor: colorsPalette.purpleCustom.dark,
    },
});

export const getAlertVariants = (): ComponentsVariants<Theme>['MuiAlert'] => {
    const variants: ComponentsVariants<Theme>['MuiAlert'] = [];

    const colors: AlertColor[] = ['error', 'info', 'success', 'warning', 'promotion'];

    for (const color of colors) {
        variants.push({
            props: { color },
            style: ({ theme }) => {
                const styles = getVariantStylesMapping(theme)[color];

                return {
                    backgroundColor: styles.backgroundColor,
                    borderColor: styles.borderColor,
                    '& .MuiAlert-icon': {
                        color: styles.iconColor,
                    },
                    '& .MuiAlertTitle-root': {
                        color: styles.titleColor,
                    },
                };
            },
        });

        variants.push({
            props: { color, variant: 'outlined' },
            style: ({ theme }) => ({
                borderColor: getVariantStylesMapping(theme)[color].borderColor,
            }),
        });
    }

    return variants;
};
